import React, { useState } from 'react';
import StoreIcon from '@mui/icons-material/Store';
import BadgeIcon from '@mui/icons-material/Badge';
import WorkIcon from '@mui/icons-material/Work';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CircularProgress from '@mui/material/CircularProgress';
import { APIURL } from '../../../configApi/apiConfig';
import axios from '../../../configApi/axiosConfig';

// Código - Estado del cupo, según la documentación de Yupana.
const ESTADO_CUPO_INFO = {
    1: {
        etiqueta: 'Aprobada',
        descripcion: 'El cupo de crédito fue aprobado.',
        Icono: CheckCircleOutlineIcon,
        clases: 'bg-emerald-50 border-emerald-200',
        clasesTexto: 'text-emerald-800',
        colorIcono: '#059669',
    },
    2: {
        etiqueta: 'Rechazada',
        descripcion: 'El cupo de crédito fue rechazado.',
        Icono: HighlightOffIcon,
        clases: 'bg-red-50 border-red-200',
        clasesTexto: 'text-red-800',
        colorIcono: '#dc2626',
    },
    3: {
        etiqueta: 'Espera Aprobación (Fábrica)',
        descripcion: 'La solicitud está pendiente de aprobación por Fábrica.',
        Icono: AccessTimeIcon,
        clases: 'bg-amber-50 border-amber-200',
        clasesTexto: 'text-amber-800',
        colorIcono: '#d97706',
    },
    4: {
        etiqueta: 'Espera Aprobación (V. Terrena)',
        descripcion: 'La solicitud está pendiente de verificación terrena.',
        Icono: AccessTimeIcon,
        clases: 'bg-amber-50 border-amber-200',
        clasesTexto: 'text-amber-800',
        colorIcono: '#d97706',
    },
};

// Yupana aún está evaluando (SolEstado "PE"): responde OK sin cupEstado. No es un
// rechazo; el backend no modifica la solicitud y hay que volver a consultar.
const ESTADO_CUPO_EN_PROCESO = {
    etiqueta: 'En proceso',
    descripcion: 'Yupana todavía está evaluando la solicitud. Vuelva a consultar en unos segundos.',
    Icono: AccessTimeIcon,
    clases: 'bg-amber-50 border-amber-200',
    clasesTexto: 'text-amber-800',
    colorIcono: '#d97706',
};

const EstadoCupoResultado = ({ resultado }) => {
    const cupEstado = resultado?.data?.cupEstado;
    const enProceso = resultado?.enProceso === true || (cupEstado == null && resultado?.data?.solEstado === 'PE');
    // En proceso el disponible llega en 0 y no es un cupo real: no se muestra
    const cupDisponible = enProceso ? null : resultado?.data?.cupDisponible;
    const observacion = resultado?.data?.solEstadoObservacion;
    const info = (enProceso ? ESTADO_CUPO_EN_PROCESO : ESTADO_CUPO_INFO[cupEstado]) || {
        etiqueta: 'Desconocido',
        descripcion: observacion || resultado?.message || 'No se pudo determinar el estado del cupo.',
        Icono: HelpOutlineIcon,
        clases: 'bg-slate-50 border-slate-200',
        clasesTexto: 'text-slate-700',
        colorIcono: '#475569',
    };
    const { Icono } = info;

    return (
        <div className={`px-6 py-3 border-b ${info.clases}`}>
            <div className="flex items-start gap-2">
                <Icono sx={{ fontSize: 18, color: info.colorIcono }} className="mt-0.5 flex-shrink-0" />
                <div className={`min-w-0 ${info.clasesTexto}`}>
                    <p className="font-semibold">
                        Estado de Cupo: {info.etiqueta}
                        {resultado?.yaResuelto && <span className="ml-1 font-normal opacity-70">(ya resuelto previamente)</span>}
                    </p>
                    <p className="text-sm">{info.descripcion}</p>
                    {cupDisponible != null && (
                        <p className="text-sm mt-0.5">
                            Disponible: <span className="font-semibold">${Number(cupDisponible).toFixed(2)}</span>
                        </p>
                    )}
                    {observacion && (
                        <p className="text-xs mt-1 opacity-80">{observacion}</p>
                    )}
                </div>
            </div>
        </div>
    );
};

// Un acento de color por dominio — el mismo lenguaje de color que ya usa
// cada paso del wizard (identidad en esmeralda, direcciones en índigo,
// referencias en rosa, contacto/laboral en ámbar) para que el resumen se
// sienta como la continuación natural del formulario, no como una pantalla
// aparte.
const SECTION_TONES = {
    blue: { icon: '#2563eb', header: 'bg-blue-50/60 border-blue-100' },
    emerald: { icon: '#059669', header: 'bg-emerald-50/60 border-emerald-100' },
    amber: { icon: '#d97706', header: 'bg-amber-50/60 border-amber-100' },
    indigo: { icon: '#4f46e5', header: 'bg-indigo-50/60 border-indigo-100' },
    rose: { icon: '#e11d48', header: 'bg-rose-50/60 border-rose-100' },
    violet: { icon: '#7c3aed', header: 'bg-violet-50/60 border-violet-100' },
};

const Section = ({ icon: Icon, title, tone = 'blue', badge, bodyClassName, children }) => {
    const t = SECTION_TONES[tone] || SECTION_TONES.blue;
    return (
        <div className="border border-slate-200 rounded-xl overflow-hidden h-fit">
            <div className={`flex items-center gap-2 px-4 py-2.5 border-b ${t.header}`}>
                <Icon sx={{ fontSize: 16, color: t.icon }} />
                <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">{title}</span>
                {badge != null && (
                    <span className="ml-auto text-[10px] font-bold text-slate-500 bg-white px-1.5 py-0.5 rounded-full border border-slate-200">
                        {badge}
                    </span>
                )}
            </div>
            <div className={bodyClassName || 'p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3'}>
                {children}
            </div>
        </div>
    );
};

const Field = ({ label, value }) => (
    <div>
        <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-0.5">{label}</p>
        <p className="text-sm text-slate-800 font-medium">{value || '—'}</p>
    </div>
);

// Versión compacta de Field para el detalle dentro de una tarjeta (direcciones)
const MiniField = ({ label, value }) => (
    <div>
        <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wide">{label}</p>
        <p className="text-xs text-slate-700">{value}</p>
    </div>
);

const DocumentPreview = ({ title, url, file }) => (
    <div className="flex-1 w-full">
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 text-center">{title}</p>
        {url ? (
            <iframe
                src={url}
                className="w-full h-[320px] border border-slate-200 rounded-lg shadow-sm bg-white"
                title={`Visor PDF ${title}`}
            />
        ) : (
            <div className="text-center py-6 text-slate-500 bg-white border border-slate-200 rounded-lg h-[320px] flex flex-col items-center justify-center">
                <PictureAsPdfIcon sx={{ fontSize: 40, opacity: 0.5, mb: 1 }} />
                <p className="text-sm px-4">Documento adjunto</p>
                {file?.name && (
                    <p className="text-xs font-mono mt-1 opacity-80 break-all px-2">{file.name}</p>
                )}
            </div>
        )}
    </div>
);

const ResumenSolicitudYupana = ({
    formData,
    direcciones,
    referencias,
    Afiliado,
    Jubilado,
    dataBodega,
    tipoConsulta,
    tiposVivienda,
    tiposRubro,
    relacionesRef,
    tiposDireccion,
    ubicaciones,
    cantonesPorDireccion,
    parroquiasPorDireccion,
    onEdit,
    onConfirm,
    onReturn,
    loading,
    isConsulta,
    continuarSolicitud,
    sCreSolicitudWeb,
    idEstadoVerificacionSolicitud,
}) => {
    const [consultandoCupo, setConsultandoCupo] = useState(false);
    const [estadoCupoResultado, setEstadoCupoResultado] = useState(null);
    const [estadoCupoError, setEstadoCupoError] = useState(null);
    const [pdfUrl, setPdfUrl] = React.useState(null);
    const [papeletaUrl, setPapeletaUrl] = React.useState(null);

    React.useEffect(() => {
        if (formData?.documentoPdf && formData.documentoPdf instanceof File) {
            const url = URL.createObjectURL(formData.documentoPdf);
            setPdfUrl(url);
            return () => URL.revokeObjectURL(url);
        } else if (typeof formData?.documentoPdf === 'string' && formData.documentoPdf.startsWith('http')) {
            setPdfUrl(formData.documentoPdf);
        }
    }, [formData?.documentoPdf]);

    React.useEffect(() => {
        if (formData?.papeletaPdf && formData.papeletaPdf instanceof File) {
            const url = URL.createObjectURL(formData.papeletaPdf);
            setPapeletaUrl(url);
            return () => URL.revokeObjectURL(url);
        } else if (typeof formData?.papeletaPdf === 'string' && formData.papeletaPdf.startsWith('http')) {
            setPapeletaUrl(formData.papeletaPdf);
        }
    }, [formData?.papeletaPdf]);

    const handleConsultarEstadoCupo = async () => {
        if (!sCreSolicitudWeb || consultandoCupo) return;
        setEstadoCupoError(null);
        setEstadoCupoResultado(null);

        // Si ya está resuelto (12 = Aprobada, 13 = Rechazada/error), no se
        // consume la API de Yupana: se muestra el estado ya conocido.
        if (idEstadoVerificacionSolicitud === 12 || idEstadoVerificacionSolicitud === 13) {
            setEstadoCupoResultado({
                yaResuelto: true,
                error: null,
                status_code: 200,
                message: 'OK',
                data: {
                    cupEstado: idEstadoVerificacionSolicitud === 12 ? 1 : 2,
                    cupDisponible: null,
                    solEstadoObservacion: null,
                },
            });
            return;
        }

        setConsultandoCupo(true);
        try {
            const url = APIURL.post_yupana_estado_cupo();
            const response = await axios.post(url, { sCre_SolicitudWeb: sCreSolicitudWeb });
            setEstadoCupoResultado(response.data);
        } catch (error) {
            setEstadoCupoError(
                error?.response?.data?.message || error?.message || 'No se pudo consultar el estado de cupo.'
            );
        } finally {
            setConsultandoCupo(false);
        }
    };

    const bodegaNombre = dataBodega.find(b => String(b.b_Bodega) === String(formData.bodega))?.b_Nombre || formData.bodega;
    const tipoConsultaNombre = tipoConsulta.find(t => String(t.idCompraEncuesta) === String(formData.tipoConsulta))?.Descripcion || formData.tipoConsulta;
    const viviendaNombre = tiposVivienda.find(v => String(v.idTipoVivienda) === String(formData.tipoViviendaId))?.Nombre
        || tiposVivienda.find(v => String(v.idTipoVivienda) === String(formData.tipoViviendaId))?.Descripcion || '';
    const rubroNombre = tiposRubro.find(r => String(r.idYupana_CategoriaNegocio) === String(formData.tipoRubroId))?.Descripcion || '';

    const nombreCompleto = [formData.apellidoPaterno, formData.apellidoMaterno].filter(Boolean).join(' ')
        + ', ' + [formData.primerNombre, formData.segundoNombre].filter(Boolean).join(' ');

    const legalAceptado = !!(formData.terminosCondiciones && formData.politicasPrivacidad);

    return (
        <div className="w-full pb-8">
            <div className="bg-white rounded-2xl shadow-xl w-full border border-slate-200">

                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-6 py-5 bg-gradient-to-r from-blue-700 to-blue-600 rounded-t-2xl">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                            <CheckCircleOutlineIcon sx={{ fontSize: 22, color: 'white' }} />
                        </div>
                        <div>
                            <p className="text-white/70 text-xs font-semibold uppercase tracking-widest">
                                {isConsulta ? 'Consulta de solicitud' : 'Revisar antes de enviar'}
                            </p>
                            <div className="flex items-center gap-2 flex-wrap">
                                <h2 className="text-white font-bold text-lg leading-tight">Resumen de la Solicitud</h2>
                                {isConsulta && (
                                    <span className="inline-flex items-center gap-1 bg-white/15 text-white text-[10px] font-bold px-2.5 py-1 rounded-full">
                                        Solo lectura
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    {continuarSolicitud && (
                        <button
                            type="button"
                            onClick={handleConsultarEstadoCupo}
                            disabled={consultandoCupo || !sCreSolicitudWeb}
                            title={!sCreSolicitudWeb ? 'No se encontró el identificador de la solicitud (sCre_SolicitudWeb).' : undefined}
                            className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold text-white bg-amber-500 border border-amber-300/70 rounded-xl shadow-md hover:bg-amber-400 hover:shadow-lg transition-all duration-200 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed flex-shrink-0"
                        >
                            {consultandoCupo
                                ? <><CircularProgress size={16} sx={{ color: 'white' }} /><span>Consultando...</span></>
                                : <><FactCheckOutlinedIcon sx={{ fontSize: 18 }} /><span>Estado de Cupo</span></>
                            }
                        </button>
                    )}
                </div>

                {estadoCupoResultado && <EstadoCupoResultado resultado={estadoCupoResultado} />}

                {estadoCupoError && (
                    <div className="px-6 py-3 border-b bg-red-50 border-red-200">
                        <div className="flex items-start gap-2">
                            <ErrorOutlineIcon sx={{ fontSize: 18, color: '#dc2626' }} className="mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-red-700 font-medium">{estadoCupoError}</p>
                        </div>
                    </div>
                )}

                {/* Body — dos columnas en escritorio para reducir el scroll de una
                    pantalla que existe para revisar rápido, no para leer de corrido. */}
                <div className="p-6 space-y-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">

                        {/* Columna izquierda — quién es y de qué vive */}
                        <div className="space-y-4">
                            <Section icon={StoreIcon} title="Datos de la Venta" tone="blue">
                                <Field label="Bodega" value={bodegaNombre} />
                                <Field label="Tipo de Consulta" value={tipoConsultaNombre} />
                            </Section>

                            <Section icon={BadgeIcon} title="Verificación de Identidad" tone="emerald">
                                <Field label="Cédula" value={formData.cedula} />
                                <Field label="Nombres completos" value={nombreCompleto} />
                                {formData.fechaNacimiento && <Field label="Fecha de Nacimiento" value={formData.fechaNacimiento} />}
                                {formData.edad && <Field label="Edad" value={`${formData.edad} años`} />}

                                {formData.conyugeCedula && (
                                    <>
                                        <div className="sm:col-span-2 border-t border-slate-100 my-1 pt-2">
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Datos del Cónyuge</p>
                                        </div>
                                        <Field label="Cédula Cónyuge" value={formData.conyugeCedula} />
                                        <Field label="Nombres Cónyuge" value={`${formData.conyugeNombres || ''} ${formData.conyugeApellidos || ''}`.trim()} />
                                        <Field label="Teléfono Cónyuge" value={formData.conyugeTelefono} />
                                        <Field label="Ingresos Cónyuge" value={formData.conyugeIngresos ? `$${Number(formData.conyugeIngresos).toFixed(2)}` : '—'} />
                                    </>
                                )}

                                {(Afiliado || Jubilado) && (
                                    <div className="flex gap-2 sm:col-span-2 mt-1">
                                        {Afiliado && <span className="bg-blue-600 text-white text-xs font-bold px-2.5 py-0.5 rounded-full">Afiliado IESS</span>}
                                        {Jubilado && <span className="bg-purple-600 text-white text-xs font-bold px-2.5 py-0.5 rounded-full">Jubilado</span>}
                                    </div>
                                )}
                            </Section>

                            <Section icon={WorkIcon} title="Contacto y Situación Laboral" tone="amber">
                                <Field label="Celular" value={formData.celular} />
                                {formData.email && <Field label="Correo Electrónico" value={formData.email} />}
                                <Field label="Formalidad" value={formData.formalidad === 'FORMAL' ? 'Formal' : 'Informal'} />
                                {formData.tipoRubroId && <Field label="Tipo de Rubro" value={rubroNombre} />}
                                <Field label="Ingreso Mensual" value={`$${Number(formData.ingreso || 0).toFixed(2)}`} />
                                {viviendaNombre && <Field label="Tipo de Vivienda" value={viviendaNombre} />}
                                {formData.resideDesde && <Field label="Reside Desde" value={formData.resideDesde} />}
                                {formData.fechaIngresoTrabajo && <Field label="Fecha Ingreso Trabajo" value={formData.fechaIngresoTrabajo} />}
                            </Section>
                        </div>

                        {/* Columna derecha — dónde está y quién lo referencia */}
                        <div className="space-y-4">
                            <Section icon={LocationOnIcon} title="Direcciones" tone="indigo" badge={direcciones.length} bodyClassName="p-4 space-y-3">
                                {direcciones.map((dir, idx) => {
                                    const tipoDirNombre = tiposDireccion.find(t => String(t.Codigo || t.id) === String(dir.tipoDireccionId))?.descripcion
                                        || tiposDireccion.find(t => String(t.Codigo || t.id) === String(dir.tipoDireccionId))?.Descripcion || '';

                                    const provObj = ubicaciones?.find(u => String(u.idCre_ProvinciaYupana || u.idProvincia) === String(dir.ubicacionId));
                                    const provNombre = provObj?.Nombre || provObj?.Descripcion || '';

                                    const cantones = cantonesPorDireccion?.[dir._id] || [];
                                    const cantonObj = cantones.find(c => String(c.idCre_CantonYupana || c.idCanton) === String(dir.cantonId));
                                    const cantonNombre = cantonObj?.Nombre || cantonObj?.Descripcion || '';

                                    const parroquias = parroquiasPorDireccion?.[dir._id] || [];
                                    const parroquiaObj = parroquias.find(p => String(p.idParroquia) === String(dir.parroquiaId));
                                    const parroquiaNombre = parroquiaObj?.Nombre || parroquiaObj?.Descripcion || '';

                                    return (
                                        <div key={dir._id} className="p-3 bg-indigo-50/60 border border-indigo-100 rounded-xl space-y-2">
                                            <div className="flex items-center gap-1.5">
                                                <LocationOnIcon sx={{ fontSize: 14, color: '#4f46e5' }} />
                                                <span className="text-xs font-bold text-indigo-700">Dirección {idx + 1}</span>
                                                {tipoDirNombre && (
                                                    <span className="text-xs text-indigo-500 bg-indigo-100 px-2 py-0.5 rounded-full">{tipoDirNombre}</span>
                                                )}
                                            </div>
                                            {dir.direccion && <p className="text-sm text-slate-700 font-medium">{dir.direccion}</p>}

                                            {/* Todo lo que el cliente ingresó a mano en el paso de Direcciones */}
                                            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 pt-2 border-t border-indigo-100">
                                                {dir.callePrincipal && <MiniField label="Calle Principal" value={dir.callePrincipal} />}
                                                {dir.calleSecundaria && <MiniField label="Calle Secundaria" value={dir.calleSecundaria} />}
                                                {dir.numeroCasa && <MiniField label="Número de Casa" value={dir.numeroCasa} />}
                                                {provNombre && <MiniField label="Provincia" value={provNombre} />}
                                                {cantonNombre && <MiniField label="Cantón" value={cantonNombre} />}
                                                {parroquiaNombre && <MiniField label="Parroquia" value={parroquiaNombre} />}
                                            </div>

                                            {dir.referencia && <p className="text-xs text-slate-500">Ref: {dir.referencia}</p>}
                                        </div>
                                    );
                                })}
                            </Section>

                            <Section icon={PeopleAltIcon} title="Referencias Personales" tone="rose" badge={referencias.length} bodyClassName="p-4 space-y-2">
                                {referencias.map((ref, idx) => {
                                    // El catálogo de relaciones usa idParentescoYupana como clave (no "id") —
                                    // con la clave equivocada esta búsqueda nunca encontraba nada y el tipo
                                    // de referencia siempre salía vacío.
                                    const relacionNombre = relacionesRef.find(r => String(r.idParentescoYupana) === String(ref.relacionId))?.Nombre
                                        || relacionesRef.find(r => String(r.idParentescoYupana) === String(ref.relacionId))?.Descripcion || '';
                                    return (
                                        <div key={ref._id} className="p-3 bg-rose-50/60 border border-rose-100 rounded-xl flex flex-wrap items-center gap-x-4 gap-y-1">
                                            <span className="text-xs font-bold text-rose-600">#{idx + 1}</span>
                                            <span className="text-sm text-slate-700 font-medium">
                                                {[ref.nombres, ref.apellidos].filter(Boolean).join(' ')}
                                            </span>
                                            {relacionNombre && (
                                                <span className="text-xs text-rose-500 bg-rose-100 px-2 py-0.5 rounded-full">{relacionNombre}</span>
                                            )}
                                            <span className="text-xs text-slate-500 font-mono ml-auto">{ref.telefono}</span>
                                        </div>
                                    );
                                })}
                            </Section>

                            {(formData.documentoPdf || pdfUrl || formData.papeletaPdf || papeletaUrl) && (
                                <Section icon={PictureAsPdfIcon} title="Documentos Adjuntos (PDF)" tone="violet" bodyClassName="p-4 bg-slate-50/50 flex flex-col sm:flex-row gap-4 items-start">
                                    <DocumentPreview 
                                        title="Cédula de Identidad" 
                                        url={pdfUrl} 
                                        file={formData.documentoPdf} 
                                    />
                                    <DocumentPreview 
                                        title="Papeleta de Votación" 
                                        url={papeletaUrl} 
                                        file={formData.papeletaPdf} 
                                    />
                                </Section>
                            )}
                        </div>
                    </div>

                    {/* Aceptaciones legales — franja compacta: para llegar aquí ya se
                        marcaron ambas casillas, así que no necesita el espacio de una
                        Section completa, solo la confirmación visual. */}
                    <div className={`flex items-center gap-2 px-4 py-3 rounded-xl border ${legalAceptado ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                        {legalAceptado
                            ? <CheckCircleOutlineIcon sx={{ fontSize: 18, color: '#059669' }} className="flex-shrink-0" />
                            : <ErrorOutlineIcon sx={{ fontSize: 18, color: '#dc2626' }} className="flex-shrink-0" />}
                        <span className={`text-sm font-medium ${legalAceptado ? 'text-emerald-800' : 'text-red-700'}`}>
                            {legalAceptado
                                ? 'Términos y condiciones y políticas de privacidad aceptadas.'
                                : 'Falta aceptar los términos y condiciones y las políticas de privacidad.'}
                        </span>
                    </div>
                </div>

                {/* Botones de acción */}
                <div className="flex flex-col sm:flex-row gap-3 px-6 py-4 bg-slate-50 border-t border-slate-200 rounded-b-2xl">
                    {!isConsulta && (
                        <button
                            type="button"
                            onClick={onEdit}
                            disabled={loading}
                            className="flex-1 flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold text-slate-700 bg-white border-2 border-slate-300 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 disabled:opacity-50"
                        >
                            <ArrowBackIcon sx={{ fontSize: 17 }} />
                            Editar información
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={isConsulta ? onReturn : onConfirm}
                        disabled={loading}
                        className={`flex-1 flex items-center justify-center gap-2 px-5 py-3 text-sm font-bold text-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-60 active:scale-95 ${isConsulta ? 'bg-slate-600 hover:bg-slate-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                    >
                        {loading
                            ? <><CircularProgress size={16} sx={{ color: 'white' }} /><span>Procesando...</span></>
                            : <>{isConsulta ? <ArrowBackIcon sx={{ fontSize: 17 }} /> : <SendIcon sx={{ fontSize: 17 }} />}<span>{isConsulta ? 'Regresar a Listado' : 'Confirmar y enviar solicitud'}</span></>
                        }
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ResumenSolicitudYupana;
